import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Stack,
} from '@mui/material';
import { Search, MapPin, Star, CheckCircle2, XCircle, HelpCircle } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { safeConsole } from '../../lib/piiProtection';
import type { MedicationSendDecision, PharmacyAvailabilityResponse } from '../../lib/api/modules/prescriptions';

interface PharmacyMedicationRef {
  id: string;
  name: string;
}

interface PharmacySelectorProps {
  selectedPharmacyId: string;
  onSelectPharmacy: (pharmacyId: string) => void;
  /** When provided together with `medications`, shows a per-medication stock-availability panel
   *  once a pharmacy is selected, and reports the doctor's decisions via `onAvailabilityChange`. */
  prescriptionId?: string;
  medications?: PharmacyMedicationRef[];
  onAvailabilityChange?: (decisions: MedicationSendDecision[]) => void;
}

interface Pharmacy {
  id: string;
  name: string;
  location?: string;
  mobile?: string;
  phone?: string;
  distance?: number;
  isInHouse?: boolean;
  city?: string;
  address?: string;
}

export function PharmacySelector({
  selectedPharmacyId,
  onSelectPharmacy,
  prescriptionId,
  medications,
  onAvailabilityChange,
}: PharmacySelectorProps) {
  const { clinic } = useAuth();
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [favorites, setFavorites] = useState<string[]>([]);

  const [availability, setAvailability] = useState<PharmacyAvailabilityResponse | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [decisions, setDecisions] = useState<Record<string, MedicationSendDecision>>({});

  const stockCheckEnabled = Boolean(prescriptionId && medications && medications.length > 0);

  useEffect(() => {
    loadPharmacies();
    loadFavorites();
  }, [clinic?.id]);

  useEffect(() => {
    if (!stockCheckEnabled || !selectedPharmacyId || !prescriptionId) {
      setAvailability(null);
      setDecisions({});
      return;
    }
    let cancelled = false;
    setAvailabilityLoading(true);
    api.prescriptions.getPharmacyAvailability(prescriptionId, selectedPharmacyId)
      .then((res) => {
        if (cancelled) return;
        const data = res.data ?? null;
        setAvailability(data);
        const initial: Record<string, MedicationSendDecision> = {};
        (data?.items ?? []).forEach((item) => {
          initial[item.prescriptionMedicationId] = {
            prescriptionMedicationId: item.prescriptionMedicationId,
            decision: item.matchType === 'EXACT_MATCH' ? 'AS_PRESCRIBED' : 'SEND_ANYWAY',
          };
        });
        setDecisions(initial);
      })
      .catch((err) => {
        safeConsole.error('Failed to check pharmacy availability:', err);
        setAvailability(null);
        setDecisions({});
      })
      .finally(() => { if (!cancelled) setAvailabilityLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPharmacyId, prescriptionId, stockCheckEnabled]);

  useEffect(() => {
    onAvailabilityChange?.(Object.values(decisions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisions]);

  const setDecisionForMedication = useCallback((prescriptionMedicationId: string, decision: MedicationSendDecision) => {
    setDecisions((prev) => ({ ...prev, [prescriptionMedicationId]: decision }));
  }, []);

  const loadPharmacies = async () => {
    setLoading(true);
    try {
      // listAssigned already unwraps to the plain array (see pharmacyPortalModule.listAssigned) —
      // it does not return a { data } envelope like most other api.* calls.
      const response = await api.pharmacy.listAssigned(clinic?.id || '');
      setPharmacies(response || []);

      // Auto-select if only one pharmacy or select last used
      if (response?.length === 1) {
        onSelectPharmacy(response[0].id);
      } else {
        const lastUsed = localStorage.getItem('lastUsedPharmacyId');
        if (lastUsed && response?.some((p: Pharmacy) => p.id === lastUsed)) {
          onSelectPharmacy(lastUsed);
        }
      }
    } catch (err) {
      safeConsole.error('Failed to load pharmacies:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = () => {
    try {
      const saved = localStorage.getItem('favoritePharmacies');
      if (saved) {
        setFavorites(JSON.parse(saved));
      }
    } catch (err) {
      safeConsole.error('Failed to load favorites:', err);
    }
  };

  const toggleFavorite = (pharmacyId: string) => {
    const newFavorites = favorites.includes(pharmacyId)
      ? favorites.filter(id => id !== pharmacyId)
      : [...favorites, pharmacyId];
    
    setFavorites(newFavorites);
    localStorage.setItem('favoritePharmacies', JSON.stringify(newFavorites));
  };

  const handleSelectPharmacy = (pharmacyId: string) => {
    onSelectPharmacy(pharmacyId);
    // Save as last used
    localStorage.setItem('lastUsedPharmacyId', pharmacyId);
  };

  const pharmacyLocation = (pharmacy: Pharmacy) =>
    pharmacy.location || [pharmacy.address, pharmacy.city].filter(Boolean).join(', ');

  const filteredPharmacies = pharmacies.filter((pharmacy) => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    return (
      pharmacy.name.toLowerCase().includes(term) ||
      pharmacyLocation(pharmacy).toLowerCase().includes(term) ||
      (pharmacy.phone || pharmacy.mobile || '').toLowerCase().includes(term)
    );
  });

  // Sort: in-house pharmacies first, then favorites, then alphabetical
  const sortedPharmacies = [...filteredPharmacies].sort((a, b) => {
    const aInHouse = Boolean(a.isInHouse);
    const bInHouse = Boolean(b.isInHouse);
    if (aInHouse && !bInHouse) return -1;
    if (!aInHouse && bInHouse) return 1;

    const aIsFav = favorites.includes(a.id);
    const bIsFav = favorites.includes(b.id);
    if (aIsFav && !bIsFav) return -1;
    if (!aIsFav && bIsFav) return 1;

    return a.name.localeCompare(b.name);
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (pharmacies.length === 0) {
    return (
      <Alert severity="warning">
        No pharmacies assigned to your clinic yet. Please contact admin to assign pharmacies.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Search */}
      {pharmacies.length > 3 && (
        <TextField
          fullWidth
          placeholder="Search by name, area or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            ),
          }}
        />
      )}

      {/* Pharmacy Select */}
      <FormControl fullWidth>
        <InputLabel>Select Pharmacy *</InputLabel>
        <Select
          value={selectedPharmacyId}
          onChange={(e) => handleSelectPharmacy(e.target.value)}
          label="Select Pharmacy *"
        >
          {sortedPharmacies.flatMap((pharmacy, idx) => {
            const items: JSX.Element[] = [];
            const prev = sortedPharmacies[idx - 1];
            const startsInHouseGroup = Boolean(pharmacy.isInHouse) && (idx === 0 || !prev?.isInHouse);
            const startsOtherGroup = !pharmacy.isInHouse && (idx === 0 || Boolean(prev?.isInHouse));
            if (startsInHouseGroup) {
              items.push(
                <ListSubheader key="header-in-house" sx={{ lineHeight: '32px' }}>
                  In-house
                </ListSubheader>
              );
            } else if (startsOtherGroup) {
              items.push(
                <ListSubheader key="header-other" sx={{ lineHeight: '32px' }}>
                  Other registered pharmacies
                </ListSubheader>
              );
            }
            items.push(
              <MenuItem key={pharmacy.id} value={pharmacy.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    {favorites.includes(pharmacy.id) && (
                      <Star size={14} fill="gold" color="gold" />
                    )}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {pharmacy.name}
                        </Typography>
                        {pharmacy.isInHouse && (
                          <Chip label="In-house" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                      {pharmacyLocation(pharmacy) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MapPin size={12} />
                          {pharmacyLocation(pharmacy)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(pharmacy.id);
                    }}
                  >
                    <Star
                      size={16}
                      fill={favorites.includes(pharmacy.id) ? 'gold' : 'none'}
                      color={favorites.includes(pharmacy.id) ? 'gold' : 'currentColor'}
                    />
                  </IconButton>
                </Box>
              </MenuItem>
            );
            return items;
          })}
        </Select>
      </FormControl>

      {/* Selected Pharmacy Info */}
      {selectedPharmacyId && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'action.hover' }}>
          {(() => {
            const selected = pharmacies.find(p => p.id === selectedPharmacyId);
            if (!selected) return null;
            
            return (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Pharmacy
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {selected.name}
                  </Typography>
                  {selected.isInHouse && (
                    <Chip label="In-house" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
                {pharmacyLocation(selected) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <MapPin size={12} />
                    {pharmacyLocation(selected)}
                  </Typography>
                )}
                {(selected.phone || selected.mobile) && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Contact: {selected.phone || selected.mobile}
                  </Typography>
                )}
                {favorites.includes(selected.id) && (
                  <Chip label="Favorite" size="small" color="warning" sx={{ mt: 1 }} />
                )}
              </Box>
            );
          })()}
        </Paper>
      )}

      {stockCheckEnabled && selectedPharmacyId && (
        <Box sx={{ mt: 2 }}>
          {availabilityLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">Checking stock at this pharmacy…</Typography>
            </Box>
          ) : !availability ? null : !availability.inventoryTracked ? (
            <Alert severity="info" icon={<HelpCircle size={18} />}>
              This pharmacy doesn't share live stock data — availability isn't shown, but sending works as usual.
            </Alert>
          ) : (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Stock at {availability.pharmacyName}</Typography>
              <Stack spacing={1.5}>
                {availability.items.map((item) => {
                  const current = decisions[item.prescriptionMedicationId];
                  return (
                    <Box key={item.prescriptionMedicationId}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {item.matchType === 'EXACT_MATCH' ? (
                          <CheckCircle2 size={16} color="#2e7d32" />
                        ) : item.matchType === 'UNAVAILABLE' ? (
                          <XCircle size={16} color="#d32f2f" />
                        ) : (
                          <HelpCircle size={16} color="#757575" />
                        )}
                        <Typography variant="body2" fontWeight={500}>{item.medicationName}</Typography>
                        <Chip
                          size="small"
                          label={
                            item.matchType === 'EXACT_MATCH' ? `In stock (${item.currentQuantity})`
                              : item.matchType === 'UNAVAILABLE' ? 'Not in stock'
                              : 'Availability unknown'
                          }
                          color={item.matchType === 'EXACT_MATCH' ? 'success' : item.matchType === 'UNAVAILABLE' ? 'error' : 'default'}
                        />
                      </Box>
                      {item.matchType === 'UNAVAILABLE' && item.substitutes.length > 0 && (
                        <FormControl size="small" sx={{ mt: 1, minWidth: 280 }}>
                          <InputLabel>Substitute (optional)</InputLabel>
                          <Select
                            label="Substitute (optional)"
                            value={current?.decision === 'SUBSTITUTE' ? current.substitutePharmacyMedicationId ?? '' : ''}
                            onChange={(e) => {
                              const chosen = item.substitutes.find(s => s.pharmacyMedicationId === e.target.value);
                              if (!chosen) {
                                setDecisionForMedication(item.prescriptionMedicationId, {
                                  prescriptionMedicationId: item.prescriptionMedicationId,
                                  decision: 'SEND_ANYWAY',
                                });
                                return;
                              }
                              setDecisionForMedication(item.prescriptionMedicationId, {
                                prescriptionMedicationId: item.prescriptionMedicationId,
                                decision: 'SUBSTITUTE',
                                substitutePharmacyMedicationId: chosen.pharmacyMedicationId,
                                substituteMedicationName: chosen.medicationName,
                              });
                            }}
                          >
                            <MenuItem value=""><em>None — send as prescribed</em></MenuItem>
                            {item.substitutes.map((s) => (
                              <MenuItem key={s.pharmacyMedicationId} value={s.pharmacyMedicationId}>
                                {s.medicationName} ({s.currentQuantity} in stock)
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          )}
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        💡 Tip: Star your frequently used pharmacies for quick access
      </Typography>
    </Box>
  );
}
